import { CheckCircleTwoTone, DownOutlined } from '@ant-design5/icons';
import { type ItemType } from 'antd5/es/menu/interface';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import { StyledTitle } from 'components/DataTable/Views/Views.style';
import { Button, Dropdown, Tooltip } from 'components/ui';
import {
  type AtlasGqlTableView,
  useConfiguredMakeModelCombinationsQuery,
  useGetBladeCardsQuery,
} from 'types/atlas-graphql';
import { uuidRegex as uuidRegexString } from 'utils/constants';

import { getRelevantBladeCards } from './getRelevantBladeCards';

const uuidRegex = new RegExp(uuidRegexString);

type BladeCardsProps = {
  tableId?: string;
  applyViewToTable: (tableViewToApply?: AtlasGqlTableView) => void;
  onSelect?: () => void;
};

export const BladeCards: React.FC<BladeCardsProps> = ({ tableId, applyViewToTable, onSelect }) => {
  const theme = useTheme();
  const { tableView } = useContext(DataTableContext);
  const location = useLocation();

  const { data: hotZonesData, loading } = useGetBladeCardsQuery();

  const isSiteDetailsPage = tableId === 'site-damage-list-table2';
  const isPotentiallyTurbineDetailsPage = tableId === 'assets-damage-list-table2';
  const assetId = location.pathname.split('/')[2];

  const { data: makeModelData } = useConfiguredMakeModelCombinationsQuery({
    variables: {
      input: {
        assetTypeName: 'Blade',
        ...(isSiteDetailsPage && uuidRegex.test(assetId) ? { siteIds: [assetId] } : {}),
        ...(isPotentiallyTurbineDetailsPage && uuidRegex.test(assetId)
          ? { ancestorAssetIds: [assetId] }
          : {}),
      },
    },
  });

  const relevantViews = getRelevantBladeCards(
    hotZonesData?.bladeHotZoneTableViews ?? [],
    makeModelData?.configuredMakeModelCombinations.existingValues ?? []
  );

  const items: ItemType[] = relevantViews.map(view => ({
    key: view.id,
    label: (
      <StyledTitle>
        {view.id === tableView?.id && <CheckCircleTwoTone twoToneColor={theme.green} />}
        <span className="blade-card-name">{view.name}</span>
      </StyledTitle>
    ),
    onClick: () => {
      if (tableId) {
        applyViewToTable({ ...view, tableName: tableId });
      }
      if (onSelect) {
        onSelect();
      }
    },
  }));

  const tooltipMessage = !!items.length
    ? 'Data indicates the damages within these regions may see higher risks of rapid damage propagation over time.'
    : 'There are no known defect regions that match your damage data. Filling in Blade Make/Model on your assets may make some available.';

  return (
    <Dropdown
      menu={{ items, style: { maxHeight: 350, overflowY: 'scroll' } }}
      trigger={['click']}
      placement="bottomLeft"
    >
      <Tooltip title={tooltipMessage}>
        <Button data-testid="blade-cards-dropdown" disabled={!items.length} loading={loading}>
          Blade Cards <DownOutlined />
        </Button>
      </Tooltip>
    </Dropdown>
  );
};
